import { useEffect, useState } from "react";
import { Input, YStack, XStack, Form, Text, Button } from "tamagui";
import { AxiosResponse } from "axios";
import liff from "@line/liff";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { RiPhoneLine, RiShieldCheckLine } from "react-icons/ri";
import RadiusCard from "@components/layout/RadiusCard";
import RadiusBoxLabel from "@components/layout/RadiusBoxLabel";
import { OTPInputBlocks } from "@components/form/OTPInputBlocks";
import { colors } from "@styles/colors";
import api, { handleRequest } from "@services/axios/apiManager";
import { useUtilsStore } from "@utils/store/utils/useUtilsTool";
import { useLineAuth } from "@utils/store/auth/useLineAuth";
import { validateTaiwanPhone } from "@components/form/validate/vaildateTaiwanPhone";
import { getSendOtpErrorMessage } from "@utils/otpError";
import { VOICE_OTP_CALLER_ID } from "@utils/func/voiceOtp";
import appToast from "@components/Toast";
import StepLayout from "../StepLayout";
import { PrimaryBtn } from "../Buttons";
import { mockMatchByPhone } from "../mockData";
import type { Supplier } from "../types";

import { maskPhone } from "@utils/func/maskPhone";

/**
 * 本地 skip-LIFF 模式（.env.development.local 的 REACT_APP_SKIP_LIFF_LOGIN=true）：
 * 沒有有效的 lineAccessToken，真實 OTP API 會回 401（「登入逾時」）。此旗標開啟時
 * 略過真實 send/verify，改用本地 mock，方便直接預覽整段流程。正式環境不受影響。
 */
const DEV_MOCK_OTP = process.env.NODE_ENV === "development" && process.env.REACT_APP_SKIP_LIFF_LOGIN === "true";

interface PhoneStepProps {
  /** OTP 驗證通過並完成「依手機配對既有瓦斯行」後回呼；supplier 為 null 表示未命中。 */
  onResult: (phone: string, phoneVerifiedTicket: string, supplier: Supplier | null) => void;
  onBack: () => void;
}

/**
 * 步驟 1：手機 OTP 驗證。沿用既有 send-phone-otp / verify-phone-otp（與 verifyPhone、
 * joinByInvite 相同）。驗證成功後「當場」依手機查既有瓦斯行（沿用全域 loading，不另跳
 * 過場畫面）：命中 → 上層導去確認頁；未命中 → 上層直接導去填地址。
 */
const PhoneStep = ({ onResult, onBack }: PhoneStepProps) => {
  const { lineAccessToken } = useLineAuth();
  const { setLoading } = useUtilsStore();
  const [sendState, setSendState] = useState<{ success?: boolean } | null>(null);
  const [countdown, setCountdown] = useState(0);
  // 語音倒數與簡訊分開：後端兩個管道各自計冷卻（簡訊 60s / 語音 90s）。
  // 共用一個倒數會在簡訊冷卻中把語音一起鎖住，而那正是收不到簡訊時最需要它的時刻。
  const [voiceCountdown, setVoiceCountdown] = useState(0);
  const [lastChannel, setLastChannel] = useState<"sms" | "voice">("sms");

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => setCountdown((p) => p - 1), 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  useEffect(() => {
    if (voiceCountdown <= 0) return;
    const timer = setInterval(() => setVoiceCountdown((p) => p - 1), 1000);
    return () => clearInterval(timer);
  }, [voiceCountdown]);

  const formSchema = z.object({
    phone: z
      .string()
      .min(1, { message: "請輸入有效的手機號碼 (09開頭共10碼) 或市話 (含區碼)" })
      .refine(validateTaiwanPhone, { message: "請輸入有效的手機號碼 (09開頭共10碼) 或市話 (含區碼)" }),
    otp: z.string().optional(),
  });
  type FormData = z.infer<typeof formSchema>;

  const {
    control,
    handleSubmit,
    setError,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: { phone: "", otp: "" },
  });

  //@ 發送驗證碼
  const onSendOtp = async (data: FormData, channel: "sms" | "voice" = "sms") => {
    //@ 本地 skip-LIFF：略過真實 API，直接進 OTP 輸入
    if (DEV_MOCK_OTP) {
      setSendState({ success: true });
      setLastChannel(channel);
      if (channel === "voice") setVoiceCountdown(90);
      else setCountdown(60);
      setValue("otp", "");
      appToast.success(channel === "voice" ? "（本地 mock）語音已撥出，任意 5 碼即可" : "（本地 mock）驗證碼已發送，任意 5 碼即可");
      return;
    }
    if (!lineAccessToken) {
      liff.login();
      return;
    }
    handleRequest({
      asyncRequest: () =>
        api.post("/app-api/line/send-phone-otp", { accessToken: lineAccessToken, phone: data?.phone || "", channel }, { isAuth: false }),
      successCallback: (res: AxiosResponse) => {
        setSendState(res.data.data);
        // 冷卻秒數以後端為準，前端寫死會與後端對不起來（倒數歸零卻仍被 429 擋）。
        const secs = Number(res?.data?.data?.resendAvailableInSeconds) || (channel === "voice" ? 90 : 60);
        if (channel === "voice") setVoiceCountdown(secs);
        else setCountdown(secs);
        setLastChannel(channel);
        setValue("otp", "");
        appToast.success(channel === "voice" ? `電話將於 30 秒內撥入（${VOICE_OTP_CALLER_ID}）` : "驗證碼已發送");
      },
      errorCallback: (error: unknown) => {
        // 語音是在 OTP 畫面按的，此時手機欄位已不在畫面上 → 掛在欄位上使用者看不到。
        if (channel === "voice") appToast.error(getSendOtpErrorMessage(error));
        else setError("phone", { type: "manual", message: getSendOtpErrorMessage(error) });
      },
      setLoading,
    });
  };

  //@ 驗證 OTP
  const onVerifyOtp = async (otpValue: string) => {
    if (!otpValue || otpValue.length !== 5) {
      setError("otp", { type: "manual", message: "請輸入 5 位數驗證碼" });
      return;
    }
    //@ 本地 skip-LIFF：任意 5 碼視為驗證通過（自行管理 loading 做手機配對）
    if (DEV_MOCK_OTP) {
      const phone = getValues("phone");
      appToast.success("（本地 mock）驗證成功");
      setLoading(true);
      const supplier = await mockMatchByPhone(phone);
      setLoading(false);
      onResult(phone, "dev-mock-ticket", supplier);
      return;
    }
    if (!lineAccessToken) return;
    const phone = getValues("phone");
    handleRequest({
      asyncRequest: () => api.post("/app-api/line/verify-phone-otp", { accessToken: lineAccessToken, phone, otpCode: otpValue }, { isAuth: false }),
      // successCallback 為 async 且 handleRequest 會 await 它，因此配對期間全域 loading 持續顯示。
      successCallback: async (res: AxiosResponse) => {
        const ticket = res?.data?.data?.phoneVerifiedTicket || "";
        appToast.success("驗證成功");
        // TODO(api): 換成後端「以手機查既有客戶綁定」endpoint。
        const supplier = await mockMatchByPhone(phone);
        onResult(phone, ticket, supplier);
      },
      errorCallback: () => setError("otp", { type: "manual", message: "驗證碼錯誤，請重新輸入" }),
      setLoading,
    });
  };

  //@ 尚未發送 → 輸入手機
  if (!sendState?.success) {
    return (
      <StepLayout title="輸入手機號碼" subtitle="我們會傳送驗證碼給您" onBack={onBack} step={1}>
        <Form onSubmit={handleSubmit((d) => onSendOtp(d))}>
          <YStack gap={15}>
            <RadiusCard>
              <RadiusBoxLabel label="手機號碼（🇹🇼 +886）" icon={<RiPhoneLine color={colors.primary} />} />
              <YStack mt="$3">
                <Text fontSize={14} fontWeight="bold" color={colors.gray} mb={4}>
                  請輸入您的手機號碼
                </Text>
                <Controller
                  control={control}
                  name="phone"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <Input
                      fontSize="$4"
                      placeholder="輸入手機號碼"
                      keyboardType="phone-pad"
                      textAlign="center"
                      letterSpacing={3}
                      value={value}
                      onBlur={onBlur}
                      borderColor={errors.phone ? "$red10" : undefined}
                      borderWidth={errors.phone ? 1 : 0}
                      onChangeText={(text: string) => onChange(text)}
                    />
                  )}
                />
                {errors.phone && (
                  <Text color="$red10" fontSize={14} mt="$2">
                    {errors.phone.message}
                  </Text>
                )}
              </YStack>
            </RadiusCard>
            <RadiusCard bg="rgba(255, 255, 255, 0.3)" border="#e2e8f0" borderRadius={10}>
              <Text fontSize={14} color={colors.gray}>
                🔒 點擊「取得驗證碼」即表示您同意 服務條款 與 隱私政策
              </Text>
            </RadiusCard>
            <PrimaryBtn onPress={handleSubmit((d) => onSendOtp(d))}>取得驗證碼</PrimaryBtn>
          </YStack>
        </Form>
      </StepLayout>
    );
  }

  //@ 已發送 → 輸入 OTP
  return (
    <StepLayout title="輸入驗證碼" subtitle={`已傳送至 ${maskPhone(getValues("phone"))}`} onBack={() => setSendState(null)} step={1}>
      <YStack gap={15}>
        <RadiusCard>
          <RadiusBoxLabel label="驗證碼" icon={<RiShieldCheckLine color={colors.primary} />} />
          <YStack mt="$3" alignItems="center">
            <Text fontSize={14} fontWeight="bold" color={colors.gray} alignSelf="flex-start">
              請輸入 5 位數驗證碼
            </Text>
            <Text fontSize={14} color={colors.gray} my="$3" fontWeight="bold">
              已傳送至 {maskPhone(getValues("phone"))}
            </Text>
            <Controller
              control={control}
              name="otp"
              render={({ field: { onChange, value } }) => (
                <OTPInputBlocks value={value || ""} onChange={onChange} onComplete={(val) => onVerifyOtp(val)} error={errors.otp?.message} />
              )}
            />
            {errors.otp && (
              <Text color="$red10" fontSize={14} mt="$2" alignSelf="center">
                {errors.otp.message}
              </Text>
            )}
            <YStack mt="$2" alignItems="center" gap="$1">
              <Button
                chromeless
                size="$3"
                color={countdown > 0 ? colors.gray : colors.primary}
                disabled={countdown > 0}
                onPress={() => onSendOtp(getValues())}
              >
                {countdown > 0 ? `重新傳送驗證碼 (${countdown}s)` : "重新傳送驗證碼"}
              </Button>
              {/* 收不到簡訊的救援管道：改由系統打電話唸驗證碼（市話也收得到）。
                  與「重新傳送」同為藍字，但不加粗、字級小一級，並以上方細線分開——
                  它是備援出口，不該和主要動作看起來像兩個同等選項。 */}
              <XStack height={1} backgroundColor="#F1F5F9" width="55%" my="$2" />
              <Button
                chromeless
                size="$2"
                color={voiceCountdown > 0 ? "#CBD5E1" : colors.primary}
                fontWeight="400"
                disabled={voiceCountdown > 0}
                onPress={() => onSendOtp(getValues(), "voice")}
              >
                {voiceCountdown > 0 ? `語音驗證 ${voiceCountdown} 秒後可再試` : "改用語音驗證"}
              </Button>
            </YStack>
          </YStack>
        </RadiusCard>
        <RadiusCard bg="rgba(255, 255, 255, 0.3)" border="#e2e8f0" borderRadius={10}>
          <Text fontSize={14} color={colors.gray}>
            {lastChannel === "voice"
              ? `📞 系統將以 ${VOICE_OTP_CALLER_ID} 撥打給您，電話中會唸兩次驗證碼`
              : "📱 簡訊通常在 30 秒內送達,請稍候。收不到可改用語音驗證"}
          </Text>
        </RadiusCard>
      </YStack>
    </StepLayout>
  );
};

export default PhoneStep;
